import { Warning, WarningAlt } from '@carbon/icons-react';
import { ClickableBanner } from 'component/banner';
import { HorizBox, Pad, PadBox } from 'component/basics';
import {
    colorSurfaceBannerInfo,
    colorSurfaceBannerWarning,
    colorSurfaceSecondary,
    colorTextHint,
    colorTextSecondaryOnBanner,
    colorTextWarning,
} from 'component/color';
import { RichText } from 'component/richtext';
import { Heading, UtilityText } from 'component/text';
import { ToggleWithLabelWidget } from 'open-modules/zdf/component/form';
import {
    EmailPermissionCategory,
    setEmailNotificationPermissionAsync,
    useEmailNotificationPermission,
} from 'open-modules/zdf/component/notifications/email-notification';
import {
    setPushNotificationPermissionAsync,
    usePushNotificationPermission,
} from 'open-modules/zdf/component/notifications/push-notification';
import {
    arePushNotificationsSupported,
    getBrowserNotificationPermission,
    isIframe,
    requestBrowserPushNotificationHandler,
} from 'open-modules/zdf/platform-specific/pushNotifs';
import { useState } from 'react';
import { StyleSheet } from 'react-native';
import { View } from 'react-native-web';
import type { AccountConfig } from 'structure/account';
import { DEPRECATED_useModuleUserGlobalData, useDatastore } from 'util/datastore';
import { logEventAsync } from 'util/eventlog';
import { useConfig } from 'util/features';
import type { Feature, TLabel } from 'util/stdtypes';
import { computedLabel, t } from 'util/stdtypes';
export type NotificationConfig = {
    pushNotificationToggleEnabled?: boolean;
    emailReplyNotificationToggleEnabled?: boolean;
    emailModNotificationToggleEnabled?: boolean;
    emailNotificationPermissions?: boolean;
};

export const AccountNotificationPermissionFeature: Feature<AccountConfig, NotificationConfig> = {
    name: 'Notification Permission',
    key: 'notificationpermission',
    providesConfig: {
        accountWidgets: [NotificationPermissionWidget],
    },
    receivesConfigDefaults: {
        pushNotificationToggleEnabled: false,
        emailNotificationPermissions: false,
        emailModNotificationToggleEnabled: false,
        emailReplyNotificationToggleEnabled: false,
    },
};

export function NotificationPermissionWidget() {
    const config = useConfig<NotificationConfig>();

    return (
        <View>
            <Pad size={40} />
            <Heading label={t('Notifications')} type='small' />
            <Pad size={24} />
            {config.pushNotificationToggleEnabled && <PushNotificationToggle />}
            <Pad size={16} />
            {config.emailNotificationPermissions && <EmailNotificationToggle />}
        </View>
    );
}

export function PushNotificationToggle() {
    const pushNotificationPermissionStatus = usePushNotificationPermission();
    const datastore = useDatastore();

    async function updatePushPermissionGlobal(permission: boolean) {
        await setPushNotificationPermissionAsync({ datastore, value: permission });
        logEventAsync(datastore, 'push-notifs-update-permission', { permission, func: 'updatePushPermissionGlobalAsync' });
    }

    return (
        <View>
            <UtilityText label={t('Push notifications')} type='small' weight='medium' />
            <Pad size={4} />
            <Pad size={12} />
            <View
                style={{
                    backgroundColor: colorSurfaceSecondary,
                    borderRadius: 8,
                }}
            >
                <ToggleWithLabelWidget
                    ariaText={'Push notification replies'}
                    labelWidget={() => <NotificationLabelWidget label='Replies' />}
                    onChange={updatePushPermissionGlobal}
                    spread
                    testID={'push-notifications-replies'}
                    value={pushNotificationPermissionStatus !== undefined ? pushNotificationPermissionStatus.status : false}
                />
            </View>
            <NoBrowserPushNotificationWarningComponent />
        </View>
    );
}

export function PushNotificationLabelWidget() {
    return (
        <View>
            <UtilityText label={t('Push notifications')} type='large' />
            <Pad size={4} />
            <UtilityText color={colorTextHint} label={t('For replies')} type='small' />
        </View>
    );
}

export function EmailNotificationToggle() {
    const datastore = useDatastore();
    const config = useConfig<NotificationConfig>();

    // Email permissions use an object with the property "status" instead of a simple boolean to handle opt-out behavior
    // useModuleUserGlobalData returns null for both non-existent values and false values
    // With a simple boolean, the toggle would either be permanently stuck on true or default to false
    const emailPermissionModerationDecision = useEmailNotificationPermission(EmailPermissionCategory.MODERATIONDECISION);
    const emailPermissionReply = useEmailNotificationPermission(EmailPermissionCategory.REPLY);

    async function updateEmailPermissionModerationDecisionAsync(permission: boolean) {
        await setEmailNotificationPermissionAsync({ datastore, category: EmailPermissionCategory.MODERATIONDECISION, value: permission });
        logEventAsync(datastore, 'email-notifs-update-permission', { permission, func: 'updateEmailPermissionModerationDecisionAsync' });
    }

    async function updateEmailPermissionReplyAsync(permission: boolean) {
        logEventAsync(datastore, 'email-notifs-update-permission', { permission, func: 'updateEmailPermissionReplyAsync' });
        await setEmailNotificationPermissionAsync({ datastore, category: EmailPermissionCategory.REPLY, value: permission });
    }
    return (
        <View>
            <UtilityText label={t('Email notifications')} type='small' weight='medium' />
            <Pad size={12} />
            <View
                style={{
                    backgroundColor: colorSurfaceSecondary,
                    borderRadius: 8,
                }}
            >
                {config.emailModNotificationToggleEnabled && (
                    <ToggleWithLabelWidget
                        ariaText='Email notification moderation decisions'
                        labelWidget={() => <NotificationLabelWidget label='Moderation decisions' />}
                        onChange={updateEmailPermissionModerationDecisionAsync}
                        spread
                        testID='email-notifications-moderation-decisions'
                        value={emailPermissionModerationDecision}
                    />
                )}
                {config.emailReplyNotificationToggleEnabled && (
                    <ToggleWithLabelWidget
                        ariaText='Email notification replies'
                        labelWidget={() => <NotificationLabelWidget label='Replies' />}
                        onChange={updateEmailPermissionReplyAsync}
                        spread
                        testID='email-notifications-replies'
                        value={emailPermissionReply}
                    />
                )}
            </View>
        </View>
    );
}

type NotificationLabelProps = {
    label: string;
};

const s = StyleSheet.create({
    labelWidget: {
        height: 48,
        paddingLeft: 12,
        justifyContent: 'center',
    },
});

export type NotificationLabelWidgetProps = NotificationLabelProps;

export function NotificationLabelWidget({ label }: NotificationLabelWidgetProps) {
    return (
        <View style={s.labelWidget}>
            <UtilityText label={t(label)} type='small' />
        </View>
    );
}

function NoBrowserPushNotificationWarningComponent() {
    // Edge case: In case of an Iframe browser permission cannot be default
    let browserPermissionInitial = getBrowserNotificationPermission();
    if (browserPermissionInitial === 'denied' && isIframe()) browserPermissionInitial = 'default';

    const [browserPermission, setBrowserPermission] = useState(browserPermissionInitial);
    const pushPermission = usePushNotificationPermission();
    const pushNotificationsSupported = arePushNotificationsSupported();
    const datastore = useDatastore();

    const updatePermissionState = () => {
        setBrowserPermission(getBrowserNotificationPermission());
    };
    const { requestPermission } = requestBrowserPushNotificationHandler({
        datastore,
        onCancel: updatePermissionState,
        onError: updatePermissionState,
        onFinish: (permission) => {
            setBrowserPermission(permission ?? 'default');
        },
    });

    const showWarning = pushNotificationsSupported && pushPermission && browserPermission !== 'granted';
    const warningText: TLabel =
        browserPermission === 'denied'
            ? t('Your browser has blocked push notifications on this page.\nYou must also unblock them in the browser settings.')
            : t('Your browser has has not accepted push notifications on this page yet. \n**Click here to grant them**');

    const clickHandler = browserPermission === 'default' ? requestPermission : null;

    return (
        <>
            {showWarning && (
                <PadBox vert={4}>
                    <ClickableBanner color={colorSurfaceBannerWarning} onPress={clickHandler} testID={'browserPushNotificationWarning'}>
                        <HorizBox center>
                            {browserPermission === 'default' ? <Warning /> : <WarningAlt />}
                            <Pad />
                            <RichText color={colorTextSecondaryOnBanner} label={warningText} />
                        </HorizBox>
                    </ClickableBanner>
                </PadBox>
            )}
        </>
    );
}
